import { AwsShapeManifests } from '../../domains/network/shapes/aws-shapes.manifest';
import { BasicShapeManifests as NetworkBasicShapeManifests } from '../../domains/network/shapes/basic-shapes.manifest';
import { ProviderShapeManifests } from '../../domains/network/shapes/provider-shapes.manifest';
import {
  getNetworkShapeLibraryDescription,
  getNetworkShapeLibraryLabel,
  isNetworkShapeLibraryId,
  NETWORK_SHAPE_LIBRARY_ORDER,
  type NetworkShapeLibraryId,
} from '../../domains/network/shapes/networkShapeLibraryCatalog';

export type { NetworkShapeLibraryId } from '../../domains/network/shapes/networkShapeLibraryCatalog';

type NetworkPickerManifest = {
  id: string;
  defaultProps?: () => unknown;
};

type NetworkPickerDefaultProps = {
  name?: unknown;
  iconUrl?: unknown;
  dialectId?: unknown;
  resourceType?: unknown;
};

export type NetworkShapePickerItem = {
  id: string;
  label: string;
  iconUrl?: string;
  libraryId: NetworkShapeLibraryId;
  resourceType?: unknown;
};

export type NetworkShapeLibraryGroup = {
  id: NetworkShapeLibraryId;
  label: string;
  description: string;
  shapes: NetworkShapePickerItem[];
};

function getNetworkPickerDefaultProps(manifest: NetworkPickerManifest): NetworkPickerDefaultProps {
  const props = manifest.defaultProps?.();
  return props && typeof props === 'object' ? props as NetworkPickerDefaultProps : {};
}

function buildNetworkBasicPickerShapes(): NetworkShapePickerItem[] {
  return NetworkBasicShapeManifests.map((manifest) => {
    const props = getNetworkPickerDefaultProps(manifest);
    return {
      id: manifest.id,
      label: typeof props.name === 'string' ? props.name : manifest.id,
      libraryId: 'base',
      resourceType: props.resourceType ?? manifest.id,
    };
  });
}

function buildAwsPickerShapes(): NetworkShapePickerItem[] {
  return AwsShapeManifests.map((manifest) => {
    const props = getNetworkPickerDefaultProps(manifest);
    return {
      id: manifest.id,
      label: typeof props.name === 'string' ? props.name : manifest.id,
      iconUrl: typeof props.iconUrl === 'string' ? props.iconUrl : undefined,
      libraryId: 'aws',
      resourceType: props.resourceType,
    };
  });
}

function buildProviderPickerShapes(): NetworkShapePickerItem[] {
  return ProviderShapeManifests.map((manifest) => {
    const props = getNetworkPickerDefaultProps(manifest);
    const dialectId = isNetworkShapeLibraryId(props.dialectId) ? props.dialectId : 'generic';
    return {
      id: manifest.id,
      label: typeof props.name === 'string' ? props.name : manifest.id,
      iconUrl: typeof props.iconUrl === 'string' ? props.iconUrl : undefined,
      libraryId: dialectId,
      resourceType: props.resourceType,
    };
  });
}

export function buildNetworkShapeLibraryGroups(): NetworkShapeLibraryGroup[] {
  const groups = new Map<NetworkShapeLibraryId, NetworkShapePickerItem[]>();
  for (const libraryId of NETWORK_SHAPE_LIBRARY_ORDER) {
    groups.set(libraryId, []);
  }

  for (const shape of [
    ...buildNetworkBasicPickerShapes(),
    ...buildAwsPickerShapes(),
    ...buildProviderPickerShapes(),
  ]) {
    groups.set(shape.libraryId, [...(groups.get(shape.libraryId) ?? []), shape]);
  }

  return NETWORK_SHAPE_LIBRARY_ORDER
    .map((id) => ({
      id,
      label: getNetworkShapeLibraryLabel(id),
      description: getNetworkShapeLibraryDescription(id),
      shapes: groups.get(id) ?? [],
    }))
    .filter((group) => group.shapes.length > 0);
}

export function orderNetworkShapeLibraryGroups<T extends { id: NetworkShapeLibraryId }>(
  groups: readonly T[],
  preferredLibraryId?: NetworkShapeLibraryId | null,
): T[] {
  if (!preferredLibraryId) return [...groups];
  return [...groups].sort((a, b) => {
    if (a.id === preferredLibraryId) return -1;
    if (b.id === preferredLibraryId) return 1;
    return 0;
  });
}

export const NETWORK_SHAPE_LIBRARY_GROUPS = buildNetworkShapeLibraryGroups();
